<template>
  <SettingsPageLayout v-if="loaded">
    <div class="flex items-center justify-between w-11/12 max-w-3xl mx-auto">
      <div class="flex items-center justify-start gap-4">
        <h1 class="block text-2xl font-bold leading-6 text-gray-900">{{ task_checklist.name }}</h1>
        <div :class="statusClass">
          {{ statusText }}
        </div>
      </div>
      <div class="flex items-center justify-end gap-4">
        <ToggleInput
          :model-value="task_checklist.status === 'enabled'"
          :label="task_checklist.status === 'enabled' ? 'Disable' : 'Enable'"
          @update:model-value="toggleChecklistStatus"
        />
        <TButton color="indigo" size="sm" label="Add task" @click="showNewModal = true" />
      </div>
    </div>

    <Draggable :items="task_checklist.checklist_tasks" :is-draggable-item="isDraggableTask" @reorder="handleReorder">
      <template #item="{ item: task }">
        <ChecklistTaskCardPreview :task="task" :task-checklist="task_checklist" :assignable-users="assignableUsers" />
      </template>

      <template #empty>
        <ZeroState>
          <p>You do not have any tasks setup yet.</p>
        </ZeroState>
      </template>
    </Draggable>

    <div class="flex justify-end w-11/12 max-w-3xl mx-auto mt-8">
      <TButton color="red" size="sm" class="flex flex-row gap-2" @click="showDeleteModal = true">
        <vue-feather type="trash" size="16" /> Delete Checklist
      </TButton>
    </div>

    <Modal
      v-if="showNewModal"
      ref="modal"
      title="New Task"
      body="Configure your task here"
      action="Create task"
      action-color="indigo"
      @close-modal="showNewModal = false"
      @handle-action="createTask"
    >
      <FormKit
        id="taskForm"
        ref="taskForm"
        type="form"
        class="grid grid-cols-1 gap-8 mt-8"
        :actions="false"
        @submit="createTask"
      >
        <FormKit id="task_name" v-model="newTask.name" type="text" label="Name" placeholder="Enter task name" />
        <QuillInput
          id="task_description"
          :model-value="newTask.description"
          label="Task Description"
          name="task-description"
          @update:model-value="value => (newTask.description = value)"
        />
        <FormKit
          id="task_type"
          type="select"
          label="Task type"
          :options="{
            basic: 'Basic',
            esignature: 'E-Signature',
            embed: 'Custom Embed Code (YouTube, Vimeo, etc.)',
            upload: 'Upload Document',
          }"
          placeholder="Select a task type"
          :model-value="newTask.task_type"
          @update:model-value="updateSelectedTaskType"
        />
        <FormKit
          v-if="newTask.task_type === 'embed'"
          id="task_embed"
          v-model="newTask.embed_src"
          type="textarea"
          rows="5"
          label="Embed Code"
          placeholder="Enter the embed code here"
        />
        <FormKit
          id="task_assignment_role"
          v-model="newTask.assigned_to_role"
          type="select"
          label="Assigned to"
          :options="{ agent: 'Agent', other: 'Other' }"
          placeholder="Select a person"
          :disabled="uploadTaskTypeSelected && true"
          :help="uploadTaskTypeSelected && 'Upload document tasks can only be assigned to an agent'"
        />
        <FormKit
          v-if="newTask.assigned_to_role === 'other'"
          id="task_assignment_other_user"
          v-model="newTask.assigned_to_user_id"
          type="select"
          label="Assigned to"
          :options="assignableUsers"
          placeholder="Select a person"
        />
      </FormKit>
    </Modal>

    <DeleteModal :show-modal="showDeleteModal" @close-modal="showDeleteModal = false" @action="deleteChecklist" />
  </SettingsPageLayout>
</template>

<script>
import SettingsPageLayout from '../SettingsPageLayout/index.vue';
import Modal from '../shared/Modal.vue';
import ZeroState from '../ZeroState.vue';
import { Api } from '../../api/index.ts';
import { formatTeamUsersForSelect } from '../../utilities/api/team';
import ChecklistTaskCardPreview from '../checklist_tasks/ChecklistTaskCardPreview.vue';
import QuillInput from '../shared/QuillInput.vue';
import ToggleInput from '../shared/ToggleInput.vue';
import DeleteModal from '../setting/company/ChecklistSettings/DeleteModal.vue';
import Draggable from '../shared/Draggable.vue';

const DEFAULT_NEW_TASK = {
  name: '',
  description: '',
  task_type: '',
  assigned_to_user_id: null,
  assigned_to_role: '',
  files: [],
  embed_src: '',
};

export default {
  components: {
    SettingsPageLayout,
    Modal,
    ZeroState,
    ChecklistTaskCardPreview,
    QuillInput,
    ToggleInput,
    DeleteModal,
    Draggable,
  },
  data() {
    return {
      loaded: false,
      task_checklist: {
        status: null,
      },
      showNewModal: false,
      newTask: {
        ...DEFAULT_NEW_TASK,
      },
      orderedTasks: [],
      assignableUsers: [],
      showDeleteModal: false,
    };
  },
  computed: {
    statusClass() {
      return this.task_checklist.status === 'disabled' ? 'badge badge-warning' : 'badge bg-green-300';
    },
    statusText() {
      return `Currently ${this.task_checklist.status}`;
    },
    checklistType() {
      return this.task_checklist.checklist_type;
    },
    uploadTaskTypeSelected() {
      return this.newTask.task_type === 'upload';
    },
  },
  async mounted() {
    await this.fetchOnboardingChecklist();
    await this.fetchAssignableUsers();
    this.$store.commit('general/hideLocalLoadingSpinner');

    if (this.task_checklist.checklist_type === 'onboarding') {
      this.$store.commit('general/setBreadcrumbs', [
        { name: 'Settings', to: '/settings' },
        { name: 'Onboarding Checklists', to: '/settings/company/onboarding_checklists' },
        { name: this.task_checklist.name },
      ]);
    } else {
      this.$store.commit('general/setBreadcrumbs', [
        { name: 'Settings', to: '/settings' },
        { name: 'Training Checklists', to: '/settings/company/training_checklists' },
        { name: this.task_checklist.name },
      ]);
    }
  },
  unmounted() {
    this.$store.commit('general/setBreadcrumbs', []);
  },
  methods: {
    updateSelectedTaskType(newTaskType) {
      this.newTask.task_type = newTaskType;
      if (newTaskType === 'upload') {
        this.newTask.assigned_to_role = 'agent';
      }
    },
    isDraggableTask(task) {
      return task.checklist_taskable_type !== 'ChecklistOnboardingFormTask';
    },
    async handleReorder({ oldIndex, newIndex, item }) {
      if (oldIndex === newIndex) return;

      // Create a copy of the tasks array to avoid mutation issues
      const tasks = [...this.task_checklist.checklist_tasks];

      // Remove the task from its old position
      const [movedTask] = tasks.splice(oldIndex, 1);

      // Insert the task at its new position
      tasks.splice(newIndex, 0, movedTask);

      // Update the local state
      this.task_checklist.checklist_tasks = tasks;

      // Persist the new order to the backend
      await this.updateTaskOrder(movedTask, newIndex);
    },

    async fetchAssignableUsers() {
      const { team_members } = await Api.Team.listTeamUsers();
      const formatted = formatTeamUsersForSelect(team_members);

      this.assignableUsers = formatted;
    },
    async updateTaskOrder(draggedChecklistTask, dropIndex) {
      // Backend ordering starts at 1...frontend ordering starts at 0...so we need to add 1 to the new index
      draggedChecklistTask.order = dropIndex + 1;
      await this.$api.ChecklistTasks.updateChecklistTask({ checklist_task: draggedChecklistTask });
    },
    async fetchOnboardingChecklist() {
      const res = await fetch(`/api/task_checklists/${this.$route.params.id}`);
      const { record } = await res.json();
      this.task_checklist = record;
      this.loaded = true;
    },
    async createTask() {
      try {
        this.$store.commit('general/showLocalLoadingSpinner');
        const res = await fetch(`/api/task_checklists/${this.$route.params.id}/checklist_tasks`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content'),
          },
          body: JSON.stringify({ checklist_task: this.newTask }),
        });

        // Wait for the response and check if it was successful
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.errors || 'Failed to create task');
        }

        this.showNewModal = false;
        this.newTask = { ...DEFAULT_NEW_TASK };

        // Now that we know the task was created successfully, fetch the updated checklist
        await this.fetchOnboardingChecklist();
        this.$store.commit('general/hideLocalLoadingSpinner');
      } catch (error) {
        console.error('Error creating task:', error);
        this.$store.commit('general/showAlert', {
          alertType: 'error',
          alertTitle: 'Error',
          alertDescription: 'Failed to create task. Please try again.',
        });
        this.$store.commit('general/hideLocalLoadingSpinner');
      }
    },
    async toggleChecklistStatus(isEnabled) {
      const newStatus = isEnabled ? 'enabled' : 'disabled';
      try {
        const res = await fetch(`/api/task_checklists/${this.$route.params.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content'),
          },
          body: JSON.stringify({
            task_checklist: {
              status: newStatus,
            },
          }),
        });

        const { record } = await res.json();
        this.task_checklist = record;
        this.$store.commit('general/showAlert', {
          alertType: 'success',
          alertTitle: 'Checklist Status Updated',
          alertDescription: `The checklist has been ${newStatus}.`,
        });
      } catch (error) {
        this.$store.commit('general/showAlert', {
          alertType: 'error',
          alertTitle: 'Error',
          alertDescription: 'Failed to update checklist status. Please try again.',
        });
      }
    },
    async deleteChecklist() {
      try {
        const { status } = await Api.TaskChecklists.deleteTaskChecklist(this.$route.params.id);

        if (status === 'success') {
          // Redirect back to the checklists index page based on type
          const route =
            this.task_checklist.checklist_type === 'onboarding'
              ? '/settings/company/onboarding_checklists'
              : this.task_checklist.checklist_type === 'offboarding'
                ? '/settings/company/offboarding_checklists'
                : '/settings/company/training_checklists';

          await this.$router.push(route);
          this.$store.commit('general/showAlert', {
            alertType: 'success',
            alertTitle: 'Checklist Deleted',
            alertDescription: 'Your checklist was successfully deleted.',
          });
        }
      } catch (error) {
        this.$store.commit('general/showAlert', {
          alertType: 'error',
          alertTitle: 'Error',
          alertDescription: 'Failed to delete checklist. Please try again.',
        });
      }
    },
  },
};
</script>
